/**
 * `StatLayer` and `StatLayerData` — the damage layers and the running numbers stats write into.
 *
 * All 14 layers come from `mmorpg_stat_layer`, and all 14 are jar entries with no pack
 * override, so the table below is CTE2's as much as it is Mine and Slash's. They are read from
 * the snapshot rather than hardcoded — a pack that adds a layer should not need an engine
 * change to have it noticed.
 *
 * A layer is not a number, it is an *accumulator with an action*. Stats spend the whole first
 * half of the event calling `add`/`reduce` on layers; nothing is applied to the damage until
 * the flush at stat-priority 30, and then they apply in `priority` order.
 */
import { CATEGORY } from "@cte2/schema";
/**
 * `StatLayer.LayerAction`. Five of them, and only two do arithmetic on the number directly —
 * the other three move damage between elements.
 */
export const LAYER_ACTIONS = [
    "ADD",
    "MULTIPLY",
    "CONVERT_PERCENT",
    "DAMAGE_TAKEN_AS",
    "X_AS_BONUS_Y_ELEMENT_DAMAGE",
];
export function isLayerAction(value) {
    return LAYER_ACTIONS.includes(value);
}
/** The layer ids the pipeline names directly (`StatLayers.java:11-37`). */
export const LAYER = {
    FLAT_DAMAGE: "flat_damage",
    DAMAGE_CONVERSION: "damage_conversion",
    ELE_AS_EXTRA_FLAT: "ele_as_extra_flat",
    ADDITIVE_DAMAGE: "additive_damage",
    DOT_DMG_MULTI: "dot_dmg_multi",
    CRIT_DAMAGE: "crit_damage",
    DOUBLE_DAMAGE: "double_damage",
    DAMAGE_TAKEN_AS: "damage_taken_as",
    ARMOR_MITIGATION: "armor_mitigation",
    PHYSICAL_MITIGATION: "physical_mitigation",
    ELEMENTAL_MITIGATION: "elemental_mitigation",
    DAMAGE_REDUCTION: "damage_reduction",
    DAMAGE_SUPPRESSION: "damage_suppression",
    DAMAGE_BLOCK: "damage_block",
    FLAT_DAMAGE_REDUCTION: "flat_damage_reduction",
};
/**
 * Cached per snapshot. Everything a `LayerIndex` hands out is immutable — `StatLayer` is a
 * plain record and `sorted()` copies — so unlike `statIndex` the whole object can be shared.
 * The mutable half of the layer system is `LayerData`, which the damage event owns.
 */
const LAYER_CACHE = new WeakMap();
export function layerIndex(snapshot) {
    const cached = LAYER_CACHE.get(snapshot);
    if (cached)
        return cached;
    const built = buildLayerIndex(snapshot);
    LAYER_CACHE.set(snapshot, built);
    return built;
}
function buildLayerIndex(snapshot) {
    const layers = new Map();
    for (const [id, entry] of Object.entries(snapshot.registries[CATEGORY.statLayer] ?? {})) {
        const data = entry.data;
        const action = data["action"];
        layers.set(id, {
            id,
            name: typeof data["name"] === "string" ? data["name"] : id,
            priority: numberAt(data, "priority") ?? 0,
            minMulti: numberAt(data, "min_multi") ?? -1,
            maxMulti: numberAt(data, "max_multi") ?? 1000,
            action: typeof action === "string" && isLayerAction(action) ? action : "MULTIPLY",
        });
    }
    const sorted = [...layers.values()].sort((a, b) => a.priority === b.priority ? a.id.localeCompare(b.id) : a.priority - b.priority);
    return { get: (id) => layers.get(id), sorted: () => [...sorted] };
}
/**
 * `StatLayerData` — one layer's accumulator for one number on one side of the event.
 *
 * The game keys these on `layer + numberID + side`, so the source's `additive_damage` and the
 * target's are separate running totals that happen to apply to the same number.
 */
export class LayerData {
    layer;
    numberId;
    side;
    recorder;
    number = 0;
    /**
     * Per-element percentages, for `CONVERT_PERCENT` and `DAMAGE_TAKEN_AS`. The game keeps these
     * in two separate `Conversion` fields on the same object; one map serves because no layer
     * uses both actions.
     */
    conversion = new Map();
    /**
     * `AdditionalConversion` — the single `{to, percent}` `X_AS_BONUS_Y_ELEMENT_DAMAGE` reads.
     * It is one element rather than a map because the game keys the whole *layer* per element
     * (`getConversionLayer`), so each target element gets its own accumulator.
     */
    additionalTo;
    constructor(layer, numberId, side, 
    /**
     * Set only when a breakdown was asked for. The accumulator is a bare float in the game,
     * so this is the only place the stat behind a `+35%` can still be named — after the flush
     * there is nothing left but the total.
     */
    recorder) {
        this.layer = layer;
        this.numberId = numberId;
        this.side = side;
        this.recorder = recorder;
    }
    /** `StatLayerData.convertDamage` / `.damageTakenAs` — both accumulate into `conversion`. */
    addConversion(element, percent) {
        const before = this.conversion.get(element) ?? 0;
        this.conversion.set(element, before + percent);
        this.record("conversion", percent, before, before + percent, element);
    }
    /**
     * The chance that *no* write to this layer happened on a given hit. Each write is its own
     * roll (`random_roll` per stat), so the misses multiply. Only a pinned layer reads it — see
     * {@link getMultiplier}.
     */
    missChance = 1;
    /** `chance` is the probability the write happened at all — 1 unless it came off a roll. */
    add(value, chance = 1) {
        const before = this.number;
        this.number += value;
        this.missChance *= 1 - chance;
        this.record("add", value, before, this.number);
    }
    reduce(value, chance = 1) {
        const before = this.number;
        this.number -= value;
        this.missChance *= 1 - chance;
        this.record("reduce", -value, before, this.number);
    }
    multiply(value) {
        const before = this.number;
        this.number *= value;
        this.record("multiply", this.number - before, before, this.number);
    }
    record(kind, value, before, after, element) {
        // `additionalTo` is the fallback because `getConversionLayer` keys a separate accumulator
        // per *target* element: without it, two "x% as extra fire" and "y% as extra cold" writes
        // into the same layer id would be indistinguishable in the trace.
        this.recorder?.write(this.layer.id, this.numberId, this.side, kind, value, before, after, element ?? this.additionalTo);
    }
    /**
     * `getNumber()` (StatLayerData.java:107-111):
     *
     *     public float getNumber() {
     *         var lay = getLayer();
     *         float num = number; // MathHelper.clamp(number, lay.min_multi, lay.max_multi);
     *         return num;
     *     }
     *
     * The clamp is commented out in the source. That is not a stale comment — it is why the two
     * `ADD` layers ignore their declared bounds entirely, so `flat_damage`'s ±1e8 and
     * `flat_damage_reduction`'s -1000 floor are dead numbers in this version. Reinstating the
     * clamp here would quietly disagree with the game on any build with large flat damage.
     */
    getNumber() {
        return this.number;
    }
    /**
     * `getMultiplier()` (StatLayerData.java:113-121) — this one *does* clamp.
     *
     *     float multi = 1 + (num / 100F);
     *     multi = MathHelper.clamp(multi, lay.min_multi, lay.max_multi);
     *
     * `double_damage` declares `min_multi == max_multi == 2`, so the clamp forces exactly ×2 the
     * moment any stat touches it — a 1% chance of double damage and a 500% one produce the same
     * multiplier once they fire.
     *
     * That makes a chance-weighted write useless on a pinned layer: 10% of a 10% double-damage
     * roll still clamps to ×2. So a pinned layer returns the expected multiplier instead,
     * `1 + (pin - 1) * P(any write fired)` — 10% double damage is ×1.1, not ×2. A layer touched
     * only by certain writes has a miss chance of 0 and gets the game's exact pin.
     */
    getMultiplier() {
        const multi = clampTo(1 + this.number / 100, this.layer.minMulti, this.layer.maxMulti);
        if (this.layer.minMulti !== this.layer.maxMulti)
            return multi;
        return 1 + (multi - 1) * (1 - this.missChance);
    }
    /**
     * `Conversion.normalizeNumbersToCapTo100()` — conversion percentages are scaled down
     * proportionally when they sum past 100, rather than the excess being dropped.
     */
    normalizedConversion() {
        const total = [...this.conversion.values()].reduce((sum, v) => sum + v, 0);
        if (total <= 100 || total === 0)
            return new Map(this.conversion);
        const scale = 100 / total;
        return new Map([...this.conversion].map(([k, v]) => [k, v * scale]));
    }
}
function clampTo(value, min, max) {
    return value < min ? min : value > max ? max : value;
}
function numberAt(node, key) {
    const v = node[key];
    return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}
//# sourceMappingURL=layers.js.map